/**
 * The client-side site-search request — the standalone, growth-oriented model the search form populates
 * and submits (CPACWEB-432 PR4). It mirrors the server-side `ca.cpac.search.SiteSearchQuery`: a search
 * {@link term}, the {@link page}, and an **extensible** list of {@link SearchFilter}s. Today the filters
 * are the sort order, the content type, and the program/tag ids; a future advanced filter is just a new
 * `kind` — the form keeps populating one query object, and this stays the single place that knows how a
 * query serializes to the request, so the search mechanism can grow without rewiring every call site.
 *
 * The search runs by a full-page GET navigation today (the server renders the results); `toParams()` is the
 * one serialization point, so the same object can later drive a `fetch` to a JSON `siteSearch` endpoint
 * with no change to the form that builds it.
 */
export interface SearchFilter {
    kind: string;
    value: string;
}

export const FILTER_ORDER = "order";
export const FILTER_TYPE = "type";
export const FILTER_PROGRAM_ID = "programId";
export const FILTER_TAG_ID = "tagId";
export const FILTER_START_DATE = "startDate";
export const FILTER_END_DATE = "endDate";

export class SiteSearchQuery {
    term: string;
    page: number;
    filters: SearchFilter[];

    constructor(term = "", page = 1, filters: SearchFilter[] = []) {
        this.term = term;
        this.page = page;
        this.filters = filters;
    }

    /** Add or replace a filter; an empty/absent value clears it (extensible — any `kind` is accepted). */
    setFilter(kind: string, value: string | null | undefined): this {
        this.filters = this.filters.filter((f) => f.kind !== kind);
        if (value !== null && value !== undefined && value !== "") {
            this.filters.push({ kind, value });
        }
        return this;
    }

    getFilter(kind: string): string | undefined {
        return this.filters.find((f) => f.kind === kind)?.value;
    }

    withPage(page: number): this {
        this.page = page;
        return this;
    }

    /** Serialize to query params (the request the site search runs). */
    toParams(): URLSearchParams {
        const params = new URLSearchParams();
        if (this.term) {
            params.set("key", this.term);
        }
        params.set("page", String(this.page));
        for (const f of this.filters) {
            if (f.value) {
                params.set(f.kind, f.value);
            }
        }
        return params;
    }

    /** Run the search: navigate to the current path with this query's params (a full-page GET). */
    submit(): void {
        window.location.assign(window.location.pathname + "?" + this.toParams().toString() + "#form");
    }

    /** Read the query the on-page search form currently represents (term + selects + hidden ids + page). */
    static fromSearchForm(): SiteSearchQuery {
        const q = new SiteSearchQuery();
        const field = document.querySelector("form.search-form div.search-box input.search-field");
        if (field instanceof HTMLInputElement) {
            q.term = field.value;
        }
        const orderSelect = document.querySelector("select#order");
        const select = orderSelect instanceof HTMLSelectElement ? orderSelect : null;
        const explicitOrderParam = select?.dataset.orderExplicit === "true";
        const shouldUseRelevance = Boolean(q.term && q.term !== "*" && select && !explicitOrderParam && !select.dataset.userChanged);
        if (shouldUseRelevance && select) {
          // Only override when the current page did not already have an explicit
          // order request parameter and the user has not manually changed this select.
          select.value = "relevance";
        }
        q.page = inputNumber("currentSearchFormPageNumber", 1);
        q.setFilter(FILTER_ORDER, shouldUseRelevance && select ? "relevance" : explicitOrderParam && select ? select.value : "desc");
        q.setFilter(FILTER_TYPE, selectValue("type"));
        q.setFilter(FILTER_PROGRAM_ID, inputValue("currentSearchProgramId"));
        q.setFilter(FILTER_TAG_ID, inputValue("currentSearchTagId"));
        q.setFilter(FILTER_START_DATE, inputValue("startDate"));
        q.setFilter(FILTER_END_DATE, inputValue("endDate"));
        return q;
    }

    /** Read the query the current URL represents — used by pagination, which preserves the active search. */
    static fromLocation(): SiteSearchQuery {
        const params = new URLSearchParams(window.location.search);
        const q = new SiteSearchQuery();
        q.term = params.get("key") ?? "";
        const orderSelect = document.querySelector("select#order");
        const select = orderSelect instanceof HTMLSelectElement ? orderSelect : null;
        const explicitOrderParam = select?.dataset.orderExplicit === "true";
        const shouldUseRelevance = Boolean(q.term && q.term !== "*" && select && !explicitOrderParam && !select.dataset.userChanged);
        if (shouldUseRelevance && select) {
          // Only override when the current page did not already have an explicit
          // order request parameter and the user has not manually changed this select.
          select.value = "relevance";
        }
        q.page = parseInt(params.get("page") ?? "1", 10) || 1;
        q.setFilter(FILTER_ORDER, shouldUseRelevance && select ? "relevance" : explicitOrderParam && select ? select.value : "desc");
        q.setFilter(FILTER_TYPE, params.get("type"));
        q.setFilter(FILTER_PROGRAM_ID, params.get("programId"));
        q.setFilter(FILTER_TAG_ID, params.get("tagId"));
        q.setFilter(FILTER_START_DATE, params.get("startDate"));
        q.setFilter(FILTER_END_DATE, params.get("endDate"));
        return q;
    }
}

function selectValue(id: string): string | null {
    const el = document.getElementById(id);
    return el instanceof HTMLSelectElement ? el.value : null;
}

function inputValue(id: string): string | null {
    const el = document.getElementById(id);
    if (el instanceof HTMLInputElement) return el.value;
    // Fallback: look for an input element with the given name (used by client-mounted hidden inputs)
    const named = document.querySelector<HTMLInputElement>(`input[name="${id}"]`);
    return named ? named.value : null;
}

function inputNumber(id: string, fallback: number): number {
    const el = document.getElementById(id);
    if (el instanceof HTMLInputElement) {
        const n = parseInt(el.value, 10);
        if (!isNaN(n)) {
            return n;
        }
    }
    return fallback;
}
